/*
 * A popover (Radix `Popover`) for anything richer than a menu — the emoji picker, a date picker,
 * a member card. It is dismissible and focus-managed; content that must block the page is a Dialog
 * instead.
 *
 * Open state is the primitive's unless the caller passes `open`: a surface that closes the panel
 * itself — a picker that has been picked from, a card that follows the pointer — hands the state
 * over and is told of every change through `onOpenChange`, instead of forcing a close by mounting
 * a fresh panel under a new key.
 *
 * It has two modes, which differ in exactly the properties that decide whether a floating panel is
 * safe (I60, B4-12). The **trigger** mode is the ordinary one: a control opens the panel on a click
 * and the panel takes the focus, because the reader asked for it and is going to use it. The
 * **dwell** mode is the hover card's: the trigger is an anchor and not a control, nothing opens on a
 * click, and the panel never takes the focus — the pointer is resting on a row, very possibly over a
 * half-typed message. Both live here rather than in a second component, because they are one floating
 * shape, and a surface that reached past this file for the primitive re-implemented the anchoring,
 * the declined focus and the pointer handlers by hand.
 */

import * as RadixPopover from "@radix-ui/react-popover"
import type { ReactNode } from "react"
import { cx } from "../lib/cx.ts"
import "./ui.css"

export interface PopoverProps {
	trigger: ReactNode
	children: ReactNode
	/** The accessible name of the panel, already translated. */
	label: string
	align?: "start" | "center" | "end"
	/** Which side of the anchor the panel opens on; the primitive flips it when there is no room. */
	side?: "top" | "bottom"
	/** Present: the caller owns the open state and hears every change through `onOpenChange`. */
	open?: boolean
	onOpenChange?: (open: boolean) => void
	/**
	 * A panel the *pointer* opens by resting on the trigger. The trigger becomes an anchor rather than
	 * a control — no `aria-expanded` on a span, no click-to-open — the panel never takes the focus, and
	 * the two handlers reach the panel itself as well, so travelling from the anchor into the panel
	 * does not close it. The caller owns the open state in this mode, because the dwell is its timer.
	 */
	dwell?: { onPointerEnter: () => void; onPointerLeave: () => void }
	/** The panel's own class, beside the two every floating panel wears: a card's own geometry. */
	className?: string
}

export function Popover({
	trigger,
	children,
	label,
	align = "start",
	side,
	open,
	onOpenChange,
	dwell,
	className,
}: PopoverProps) {
	return (
		<RadixPopover.Root
			{...(open === undefined ? {} : { open })}
			{...(onOpenChange === undefined ? {} : { onOpenChange })}
		>
			{dwell === undefined ? (
				<RadixPopover.Trigger asChild>{trigger}</RadixPopover.Trigger>
			) : (
				<RadixPopover.Anchor asChild>{trigger}</RadixPopover.Anchor>
			)}
			<RadixPopover.Portal>
				<RadixPopover.Content
					className={cx("tb-elevated tb-surface tb-popover", className)}
					sideOffset={6}
					align={align}
					{...(side === undefined ? {} : { side })}
					aria-label={label}
					{...(dwell === undefined
						? {}
						: {
								onOpenAutoFocus: (event: Event) => event.preventDefault(),
								onCloseAutoFocus: (event: Event) => event.preventDefault(),
								onPointerEnter: dwell.onPointerEnter,
								onPointerLeave: dwell.onPointerLeave,
							})}
				>
					{children}
				</RadixPopover.Content>
			</RadixPopover.Portal>
		</RadixPopover.Root>
	)
}
